"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { invalidate } from "@/lib/use-fetch";
import { extractPlaceholders, renderTemplate } from "@/lib/template";
import { estimateSegments } from "@/lib/segments";
import { Badge, Button, Card, EmptyState, Input, Modal, PageHeader, Skeleton, Textarea } from "@/components/ui";
import { useToast } from "@/components/toast";
import { useConfirm } from "@/components/confirm";
import type { MessageTemplate } from "@/lib/types";

const SAMPLE: Record<string, string> = { name: "Ali", city: "Mogadishu", amount: "500", code: "8421" };

export default function TemplatesPage() {
  const toast = useToast();
  const confirm = useConfirm();
  const [rows, setRows] = useState<MessageTemplate[] | null>(null);
  const [editing, setEditing] = useState<{ templateId?: number; name: string; body: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api<{ templates: MessageTemplate[] }>("account/templates")
      .then((r) => setRows(r.templates || []))
      .catch(() => setRows([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function save() {
    if (!editing?.name.trim()) { toast.error("Give the template a name."); return; }
    if (!editing.body.trim()) { toast.error("The template body can't be empty."); return; }
    setSaving(true);
    try {
      await api("account/templates", {
        method: "POST",
        body: { templateId: editing.templateId ?? null, name: editing.name.trim(), body: editing.body }
      });
      toast.success(editing.templateId ? "Template updated." : "Template saved.");
      setEditing(null);
      load();
      invalidate("account/templates"); // keep the shared cache (Use-template picker) fresh
    } catch (e) { toast.error((e as Error).message); }
    finally { setSaving(false); }
  }

  async function remove(t: MessageTemplate) {
    const ok = await confirm({
      title: "Delete template",
      message: <>Delete <strong>{t.name}</strong>? Any campaign already sent with it is unaffected.</>,
      confirmLabel: "Delete",
      danger: true
    });
    if (!ok) return;
    try {
      await api("account/templates", { method: "DELETE", body: { templateId: t.templateId } });
      toast.success(`Deleted "${t.name}".`);
      load();
      invalidate("account/templates");
    } catch (e) { toast.error((e as Error).message); }
  }

  // Live preview of the template being edited, with sample values filled in.
  const placeholders = useMemo(() => extractPlaceholders(editing?.body ?? ""), [editing?.body]);
  const preview = useMemo(() => {
    const vars: Record<string, string> = {};
    for (const p of placeholders) vars[p] = SAMPLE[p] ?? p.toUpperCase();
    return renderTemplate(editing?.body ?? "", vars);
  }, [editing?.body, placeholders]);
  const seg = useMemo(() => estimateSegments(preview), [preview]);

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <PageHeader
        title="Templates"
        subtitle="Reusable messages you can drop into any send. Use {{placeholders}} to personalize them."
        actions={<Button onClick={() => setEditing({ name: "", body: "" })}>New template</Button>}
      />

      <Card bodyClassName="p-0">
        {rows === null ? (
          <div className="space-y-2 p-5">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-14" />)}</div>
        ) : rows.length === 0 ? (
          <div className="p-5">
            <EmptyState
              title="No templates yet"
              description="Save a message you send often — then load it on the Send, Bulk or Personalized pages instead of retyping it."
              action={<Button onClick={() => setEditing({ name: "", body: "" })}>Create your first template</Button>}
            />
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {rows.map((t) => {
              const ph = extractPlaceholders(t.body);
              return (
                <li key={t.templateId} className="flex items-start justify-between gap-4 px-5 py-4">
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900">{t.name}</p>
                    <p className="mt-0.5 truncate text-sm text-gray-600">{t.body}</p>
                    {ph.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {ph.map((p) => <Badge key={p} tone="blue">{`{{${p}}}`}</Badge>)}
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button size="sm" variant="secondary" onClick={() => setEditing({ templateId: t.templateId, name: t.name, body: t.body })}>
                      Edit
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(t)}>Delete</Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={editing?.templateId ? "Edit template" : "New template"}
        widthClass="max-w-xl"
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save template"}</Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input
            label="Name"
            value={editing?.name ?? ""}
            onChange={(e) => setEditing((t) => (t ? { ...t, name: e.target.value } : t))}
            placeholder="e.g. Order confirmation"
            autoFocus
          />
          <Textarea
            label="Message"
            rows={4}
            value={editing?.body ?? ""}
            onChange={(e) => setEditing((t) => (t ? { ...t, body: e.target.value } : t))}
            placeholder="Hi {{name}}, your order is ready for pickup in {{city}}."
            hint="Wrap a variable in double braces, e.g. {{name}}. It's filled in per recipient on a personalized send."
          />

          {placeholders.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-gray-500">Placeholders:</span>
              {placeholders.map((p) => <Badge key={p} tone="blue">{`{{${p}}}`}</Badge>)}
            </div>
          )}

          <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3 dark:bg-gray-500/10">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Preview (sample values)</p>
            <p className="whitespace-pre-wrap text-sm text-gray-800">{preview || "—"}</p>
            <p className="mt-2 text-xs text-gray-400">
              {seg.chars} chars · {seg.segments} SMS segment{seg.segments === 1 ? "" : "s"} · {seg.encoding}
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
}
